import JSZip from 'jszip';
import { buildSurface } from './surface.js';
import { buildRibbons } from './ribbons.js';
import { parseBumpDip } from './defects.js';
import Papa from 'papaparse';
import { centeredMovingAverage, sampleRateFromHeader, IRI_WINDOW_FEET } from './movingAverage.js';

export const MAX_ACF_BYTES = 100 * 1024 * 1024;
const MAX_PTS_BYTES = 150 * 1024 * 1024;

export function parsePoints(text, { samplesPerFoot = null } = {}) {
  const end = text.indexOf('\n');
  if (end < 0) throw new Error('The PTS entry contains no readings.');
  const headers = Papa.parse(text.slice(0, end).trim()).data[0].map(h => h.trim());
  const latIndex = headers.findIndex(h => /^latitude\b/i.test(h));
  const lngIndex = headers.findIndex(h => /^longitude\b/i.test(h));
  const channels = headers.map((name, index) => ({ name, index })).filter(h => h.index !== latIndex && h.index !== lngIndex);
  if (latIndex < 0 || lngIndex < 0 || !channels.length) throw new Error('PTS must contain latitude, longitude, and measurement columns.');
  // Keep full-resolution samples in typed buffers. Compute distance-based IRI
  // averages before retaining any representative locations for rendering.
  let capacity = 1;
  for (let i = end + 1; i < text.length; i++) if (text[i] === '\n') capacity++;
  const latitudes = new Float64Array(capacity);
  const longitudes = new Float64Array(capacity);
  const stations = new Float64Array(capacity);
  const segments = new Uint32Array(capacity);
  const raw = channels.map(() => new Float64Array(capacity));
  const spacing = Number.isFinite(samplesPerFoot) && samplesPerFoot > 0 ? 1 / samplesPerFoot : null;
  let segment = 0, breakBeforeNext = false;
  let count = 0, skipped = 0, negativeIriCount = 0;
  const bounds = { north: -90, south: 90, east: -180, west: 180 };
  const available = channels.map(() => false);
  let start = end + 1;
  while (start < text.length) {
    let stop = text.indexOf('\n', start);
    if (stop < 0) stop = text.length;
    const line = text.slice(start, stop).trim(); start = stop + 1;
    if (!line) continue;
    const row = line.split(',');
    const lat = row[latIndex]?.trim() ? Number(row[latIndex]) : NaN;
    const lng = row[lngIndex]?.trim() ? Number(row[lngIndex]) : NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) { skipped++; breakBeforeNext = true; continue; }
    const values = channels.map((channel, i) => {
      const raw = row[channel.index]?.trim();
      const value = raw ? Number(raw) : NaN;
      if (Number.isFinite(value)) { available[i] = true; return value; }
      return null;
    });
    if (values.every(v => v === null)) { skipped++; breakBeforeNext = true; continue; }
    if (values.some((v, i) => v !== null && v < 0 && /\biri\b/i.test(channels[i].name))) negativeIriCount++;
    const previous = count - 1;
    const dLat = previous >= 0 ? (lat - latitudes[previous]) * 364000 : 0;
    const dLng = previous >= 0 ? (lng - longitudes[previous]) * 364000 * Math.cos(lat * Math.PI / 180) : 0;
    const gpsFeet = Math.hypot(dLat, dLng);
    if (breakBeforeNext || gpsFeet > IRI_WINDOW_FEET) segment++;
    stations[count] = previous < 0 || segments[previous] !== segment ? 0 : stations[previous] + (spacing ?? gpsFeet);
    segments[count] = segment;
    latitudes[count] = lat; longitudes[count] = lng;
    values.forEach((value, i) => { raw[i][count] = value ?? NaN; });
    count++; breakBeforeNext = false;
    bounds.north = Math.max(bounds.north, lat); bounds.south = Math.min(bounds.south, lat);
    bounds.east = Math.max(bounds.east, lng); bounds.west = Math.min(bounds.west, lng);
  }
  if (!count) throw new Error('No valid geolocated measurements were found in PTS.');
  const averaged = channels.map((c, i) => /\biri\b/i.test(c.name)
    ? centeredMovingAverage(raw[i], stations, segments, count)
    : raw[i]);
  const ribbonSamples = [];
  const surfaceSamples = [];
  let lastSurfaceIndex = -1;
  let lastRibbonIndex = -1;
  let cells = new Map();
  let cellSize = 0.00001;
  const cellKey = (lat, lng) => `${Math.floor(lat / cellSize)},${Math.floor(lng / cellSize)}`;
  for (let i = 0; i < count; i++) {
    if (cells.size >= 100000) {
      cellSize *= 2;
      const coarser = new Map();
      for (const point of cells.values()) {
        const key = cellKey(point.position[1], point.position[0]);
        if (!coarser.has(key)) coarser.set(key, point);
      }
      cells = coarser;
    }
    const lat = latitudes[i], lng = longitudes[i];
    const sample = {
      position: [lng, lat], station: stations[i], segment: segments[i],
      values: averaged.map(values => Number.isFinite(values[i]) ? values[i] : null),
      rawValues: raw.map(values => Number.isFinite(values[i]) ? values[i] : null),
    };
    if (lastRibbonIndex < 0 || segments[i] !== segments[lastRibbonIndex] || stations[i] - stations[lastRibbonIndex] >= 2 || i === count - 1 || segments[i + 1] !== segments[i]) {
      ribbonSamples.push(sample); lastRibbonIndex = i;
    }
    // Use denser support for the interpolated surface without multiplying
    // the ribbon geometry used by the optional pass overlay.
    if (lastSurfaceIndex < 0 || segments[i] !== segments[lastSurfaceIndex] || stations[i] - stations[lastSurfaceIndex] >= .5 || i === count - 1 || segments[i + 1] !== segments[i]) {
      surfaceSamples.push(sample); lastSurfaceIndex = i;
    }
    const key = cellKey(lat, lng);
    if (!cells.has(key)) cells.set(key, {
      position: [lng, lat],
      values: averaged.map(values => Number.isFinite(values[i]) ? values[i] : null),
      rawValues: raw.map(values => Number.isFinite(values[i]) ? values[i] : null),
    });
  }
  const ribbonData = buildRibbons(ribbonSamples);
  return {
    surface: buildSurface(surfaceSamples, ribbonData.widthFeet),
    ribbons: ribbonData.ribbons, ribbonWidthFeet: ribbonData.widthFeet,
    channels: channels.map((c, i) => ({ ...c, available: available[i] })),
    points: [...cells.values()], count, skipped, negativeIriCount, bounds,
    smoothing: { windowFeet: IRI_WINDOW_FEET, samplesPerFoot: spacing ? samplesPerFoot : null, distanceSource: spacing ? 'sample-rate' : 'gps' },
  };
}

export async function parseAcf(buffer) {
  if (buffer.byteLength > MAX_ACF_BYTES) throw new Error('Please choose an ACF smaller than 100 MB.');
  const zip = await JSZip.loadAsync(buffer);
  const entries = Object.values(zip.files).filter(f => !f.dir);
  const matching = name => entries.filter(f => f.name.split(/[/\\]/).pop().toLowerCase() === name);
  const pts = matching('pts').concat(entries.filter(f => /\.pts$/i.test(f.name)));
  if (pts.length !== 1) throw new Error('Expected exactly one PTS point-cloud entry in this ACF.');
  // Check declared sizes before inflating. Do not decompress Maps.kml or PPF.
  if (!Number.isFinite(pts[0]._data?.uncompressedSize) || pts[0]._data.uncompressedSize > MAX_PTS_BYTES) throw new Error('The embedded point cloud exceeds the 150 MB limit.');
  const header = matching('header.csv')[0];
  if (header?._data?.uncompressedSize > 1024 * 1024) throw new Error('The ACF header is too large.');
  const headerText = header ? await header.async('string') : '';
  const date = headerText.match(/Date:\s*(\d{2})-(\d{2})-(\d{4})/);
  const result = parsePoints(await pts[0].async('string'), { samplesPerFoot: sampleRateFromHeader(headerText) });
  const defectFile = matching('bumpdip.csv')[0];
  let report = { defects: [], skipped: 0 };
  if (defectFile) {
    if (defectFile._data?.uncompressedSize > 10 * 1024 * 1024) report.warning = 'Bump/dip report exceeds the 10 MB limit.';
    else {
      try { report = parseBumpDip(await defectFile.async('string')); }
      catch { report.warning = 'Bump/dip report could not be read.'; }
    }
  }
  return { ...result, defects: report.defects, defectWarning: report.warning || null, collectionDate: date ? `${date[3]}-${date[1]}-${date[2]}` : null, headerText };
}
